import type { TelanganaDistrictKey } from "./data/telanganaDistricts";

export type ApplicationStatus =
  | "pending_validation"
  | "on_hold"
  | "ready_for_icici"
  | "ready_for_mou"
  | "icici_recorded"
  | "subcontractor_linked";

/** Per-application: assigned engineer from master roster (parallel to subcontractor). */
export interface EngineerLinkage {
  engineerMasterId: string | null;
  /** Set when officer records the assignment (mirrors subcontractor linkedAt). */
  linkedAt?: string;
}

/** One-time onboarding — site engineers assigned to beneficiaries (separate from Agent master). */
export interface EngineerMaster {
  id: string;
  fullName: string;
  phone: string;
  email: string;
  employeeCode: string;
  notes: string;
  createdAt: string;
}

/** One-time onboarding (Subcontractor Onboarding screen) — reused across beneficiary files */
export interface SubcontractorMaster {
  id: string;
  contactPersonName: string;
  firmName: string;
  /** Principal / place-of-business address from GST verification when available. */
  registeredAddress: string;
  firmRegistrationId: string;
  gstNumber: string;
  contactPhone: string;
  contactEmail: string;
  /** 12-digit Aadhaar (digits only in API responses). */
  aadhaarNumber: string;
  bankAccountNumber: string;
  bankIfsc: string;
  /** Bank branch name (after IFSC in forms). */
  bankBranchName: string;
  /** Bank / institution name (e.g. State Bank of India). */
  bankName: string;
  /** Account holder name as on passbook or cheque. */
  bankAccountName: string;
  cancelChequeFileName: string | null;
  cancelChequeStored?: boolean;
  aadharDocFileName: string | null;
  aadharDocStored?: boolean;
  gstCertFileName: string | null;
  gstCertStored?: boolean;
  /** Udyam / MSME registration certificate (scan or PDF). */
  udyamMsmeFileName: string | null;
  udyamMsmeStored?: boolean;
  /** Labour licence (scan or PDF). */
  labourLicenseFileName: string | null;
  labourLicenseStored?: boolean;
  /** Outbound only when saving a newly picked file (stripped from persisted state). */
  cancelChequeBase64?: string | null;
  cancelChequeMimeType?: string | null;
  aadharDocBase64?: string | null;
  aadharDocMimeType?: string | null;
  gstCertBase64?: string | null;
  gstCertMimeType?: string | null;
  udyamMsmeBase64?: string | null;
  udyamMsmeMimeType?: string | null;
  labourLicenseBase64?: string | null;
  labourLicenseMimeType?: string | null;
  portalToken?: string | null;
  createdAt: string;
}

/** One village from the TG registration directory assigned to a field agent. */
export interface AgentAssignedVillage {
  /** Geo directory `district_key` (slug); used with mandal/village for UI pickers when present. */
  district: string;
  mandalName: string;
  villageName: string;
  /** Stable `geo_directory_village.id` — primary link to the agent (not the name strings). */
  villageDbId?: number | null;
  /** Official registration district code (stored in DB; joined from geo when listing). */
  registrationDistrictCode?: string;
  registrationDistrictName?: string;
  mandalId?: number | null;
  mandalCode?: string;
  villageCode?: string;
}

/** Field agent master — contact details + villages they cover (Agent master screen). */
export interface AgentMaster {
  id: string;
  displayName: string;
  phone: string;
  email: string;
  employeeCode: string;
  notes: string;
  /** From server only — whether a password hash is stored */
  hasPassword: boolean;
  /** random: each onboarding email issues a new generated password; custom: admin-set password, re-entered when emailing */
  passwordMode: "random" | "custom";
  /** ISO timestamp of last onboarding email, or null */
  onboardingEmailSentAt: string | null;
  assignedVillages: AgentAssignedVillage[];
  createdAt: string;
}

/** POST/PATCH body: profile fields plus optional password change (never returned from API). */
export type AgentMasterUpsertPayload = Pick<
  AgentMaster,
  "displayName" | "phone" | "email" | "employeeCode" | "notes" | "assignedVillages" | "passwordMode"
> & {
  newPassword?: string;
  clearPassword?: boolean;
};

/** Per-application: pick a master record; work order attachment is optional */
export interface SubcontractorLinkage {
  subcontractorMasterId: string | null;
  workOrderFileName: string | null;
  /** Set when server has work-order bytes in DB (not sent as base64 in list payloads). */
  workOrderFileStored?: boolean;
  workOrderMimeType?: string | null;
  /** Outbound only: raw base64 when uploading a new work order; stripped after save. */
  workOrderFileBase64?: string | null;
  linkedAt?: string;
}

export type ValidationTriState = boolean | null;

/** Workman card: same workflow pass as verified OK, or “need to apply” (card pending but case can advance). */
export type WorkmanCardTriState = ValidationTriState | "need_to_apply";

export function isWorkmanCardPassing(w: WorkmanCardTriState | undefined): boolean {
  return w === true || w === "need_to_apply";
}

export interface WorkDoneBill {
  id: string;
  fileName: string;
  uploadedAt: string;
  /** Site person must re-confirm Aadhaar seeding after each new bill */
  seedingReVerified: boolean;
  sitePersonConfirmedAt?: string;
}

/** Server-side MOU attachment row (bytes fetched via `/mou-file/:id`). */
export interface MouAttachmentRef {
  id: string;
  fileName: string;
}

export interface MouFileUploadPart {
  fileName: string;
  mouFileBase64: string;
  mouMimeType: string | null;
}

export interface HoFields {
  iciciAccountNumber: string;
  iciciIfsc: string;
  iciciBranch: string;
  mouFileName: string | null;
  /** Set when server has MOU bytes in DB (not sent in list payloads as base64). */
  mouFileStored?: boolean;
  /** MIME type of stored MOU (from server or chosen on upload). */
  mouMimeType?: string | null;
  /** When server uses multi-file table — download/view by id. */
  mouAttachments?: MouAttachmentRef[];
  /** Outbound only: raw base64 (no data: prefix) sent on PUT when user uploads a new MOU. Stripped after save. */
  mouFileBase64?: string | null;
  /** Outbound PUT: new files (multi-upload). Stripped after save. */
  mouFilesUpload?: MouFileUploadPart[];
  /** Outbound PUT: server attachment ids to drop on next save (omit to keep all existing). */
  mouRemovedAttachmentIds?: string[];
}

/** PATCH `/api/applications/:id/ho-banking` — only properties sent are updated on the server. */
export interface HoBankingPatch {
  iciciAccountNumber?: string;
  iciciIfsc?: string;
  iciciBranch?: string;
  mouFilesUpload?: MouFileUploadPart[];
  mouRemovedAttachmentIds?: string[];
}

export interface Validations {
  /** Beneficiary name matches Aadhaar, bank, and sanction */
  nameMatchAcrossSources: ValidationTriState;
  aadhaarVerified: ValidationTriState;
  bankAccountVerified: ValidationTriState;
  workmanCardAcceptable: WorkmanCardTriState;
  /** Required before ICICI details can be saved */
  aadhaarSeedingConfirmed: boolean;
  seedingConfirmedAt?: string;
}

/**
 * PATCH `/api/applications/:id/dossier` — only top-level / nested keys sent are updated.
 * Other dossier columns and child rows stay unchanged.
 */
export interface ApplicationDossierPatch {
  district?: Application["district"];
  mandal?: string;
  village?: string;
  status?: ApplicationStatus;
  receivedAt?: string;
  leadId?: string | null;
  mobile?: Partial<MobilePayload>;
  validations?: ValidationsPatch;
  holdReasons?: string[];
  ho?: HoBankingPatch;
  subcontractor?: Partial<SubcontractorLinkage> & {
    workOrderFileBase64?: string | null;
    workOrderMimeType?: string | null;
  };
  engineer?: Partial<EngineerLinkage>;
  workDoneBillAppend?: { id?: string; fileName: string };
  workDoneBillConfirmSeeding?: { billId: string };
  milestoneItem?: {
    templateId: string;
    templateItemId: string;
    completedAt?: string | null;
    workDoneBillId?: string | null;
  };
}

/** PATCH `/api/applications/:id/validations` — only properties sent are updated on the server. */
export interface ValidationsPatch {
  nameMatchAcrossSources?: ValidationTriState;
  aadhaarVerified?: ValidationTriState;
  bankAccountVerified?: ValidationTriState;
  workmanCardAcceptable?: WorkmanCardTriState;
  aadhaarSeedingConfirmed?: boolean;
  holdReasons?: string[];
}

/** One photo (or file URL) synced from `ho_application.Photo_Urls` JSON. */
export interface MobilePhotoFromDb {
  label: string;
  url: string;
}

export interface MobilePayload {
  beneficiaryName: string;
  fatherOrHusbandName: string;
  aadhaarNumber: string;
  applicationId: string;
  houseSanctionNumber: string;
  bankName: string;
  bankAccountNumber: string;
  ifscCode: string;
  documentsFromMobile: string[];
  /** Parsed from `ho_application.Photo_Urls` (LONGTEXT JSON); empty when column missing or null. */
  photosFromMobile: MobilePhotoFromDb[];
}

/** Required item types inside a master milestone (same for every beneficiary). */
export type MilestoneItemKind = "material" | "subcontractor_bill" | "govt_bill" | "work_done_bill";

/** One row in milestone master — defines what every beneficiary must complete for that phase. */
export interface MilestoneTemplateItem {
  id: string;
  kind: MilestoneItemKind;
  label: string;
}

/** Master milestone (e.g. Phase A) — shared across all beneficiary files. */
export interface MilestoneTemplate {
  id: string;
  title: string;
  notes: string;
  sortOrder: number;
  items: MilestoneTemplateItem[];
  createdAt: string;
  /**
   * Optional links to rows in Timeline master (construction Gantt levels).
   * Used for cross-reference only; checklist items are independent of the Gantt.
   */
  linkedConstructionTimelinePhaseIds: string[];
  /**
   * Optional links to rows in Standard resources master (by resource id).
   */
  linkedResourceIds: string[];
}

/** One line item under a standard resource — label + exact quantity + unit. */
export interface StandardResourceItem {
  id: string;
  label: string;
  quantity: number;
  unit: string;
  /** When set, matches `tabStandardItemMaster.standard_item_id` (e.g. STD-00001). */
  catalogStandardItemId?: string | null;
  /** Unit rate (₹) from master; copied to PO and issue-material lines when generated. */
  rate: number;
  /** HSN/SAC from master; copied onto PO lines when PO is generated. */
  hsnCode: string;
}

/** Standard resource (master) — groups item lines with quantities; milestones link to the whole resource. */
export interface StandardResource {
  id: string;
  title: string;
  notes: string;
  sortOrder: number;
  items: StandardResourceItem[];
  createdAt: string;
}

/** Per-beneficiary completion for one template item. */
export interface MilestoneItemProgress {
  templateItemId: string;
  completedAt: string | null;
  /** For work_done_bill items — which dossier bill satisfies this slot */
  workDoneBillId: string | null;
}

/** Per-beneficiary progress for one master milestone. */
export interface ApplicationMilestoneProgress {
  templateId: string;
  itemProgress: MilestoneItemProgress[];
  milestoneCompletedAt: string | null;
}

/** One mandal line inside a zone (IGRS directory names). */
export interface GeoZoneMandalRef {
  district: string;
  mandalName: string;
}

/** One village line inside a zone (IGRS directory names). */
export interface GeoZoneVillageRef {
  district: string;
  mandalName: string;
  villageName: string;
}

/**
 * Officer-defined geographic zone: whole districts, and/or specific mandals / villages.
 * Lists are independent (a zone may mix all three levels).
 */
export interface GeoZone {
  id: string;
  name: string;
  notes: string;
  /** Entire registration district included in the zone (`geo_directory_district.district_key`). */
  districtKeys: string[];
  /** Specific mandals (use when the whole district is not in `districtKeys`). */
  mandalRefs: GeoZoneMandalRef[];
  /** Specific villages (finest grain). */
  villageRefs: GeoZoneVillageRef[];
  sortOrder: number;
  createdAt: string;
}

/** User-defined category for zone groups (e.g. disbursement batch, field cluster). */
export interface ZoneGroupCategory {
  id: string;
  name: string;
  notes: string;
  sortOrder: number;
  createdAt: string;
}

/**
 * A logical group tied to one geographic zone. Every beneficiary whose address matches the zone
 * is treated as belonging to each group tagged on that zone (for downstream filters / reporting).
 */
export interface ZoneGroup {
  id: string;
  zoneId: string;
  categoryId: string;
  name: string;
  notes: string;
  sortOrder: number;
  createdAt: string;
}

/** How a store/yard is tied to geography for beneficiary → depot resolution. */
export type StoreYardScopeType = "district" | "mandal" | "village" | "zone";

/**
 * Physical store or yard: coverage is defined at one scope level.
 * Use `resolveStoreYardForApplication` (client) to pick which store serves a beneficiary when several could match.
 */
export interface StoreYard {
  id: string;
  name: string;
  notes: string;
  /** Physical address for PO “Ship to” when this store is selected (print only). */
  address: string;
  scopeType: StoreYardScopeType;
  /** When `scopeType === "district"`: whole districts served by this store (`district_key` from geo directory). */
  districtKeys: string[];
  /** When `scopeType === "mandal"`: specific mandals (plus district on each ref). */
  mandalRefs: GeoZoneMandalRef[];
  /** When `scopeType === "village"`: specific villages. */
  villageRefs: GeoZoneVillageRef[];
  /** When `scopeType === "zone"`: ids of rows from geographic zones master (`GeoZone.id`). */
  zoneIds: string[];
  sortOrder: number;
  createdAt: string;
}

/**
 * One row in construction timeline master — level name and planned duration; drives beneficiary Gantt.
 * When loaded from MySQL, `id` is the string form of `master_construction_timeline_phase.id` (stable DB primary key).
 */
export interface ConstructionTimelinePhase {
  id: string;
  title: string;
  /** Planned duration in calendar days (sequential bar width on the chart). */
  approxDays: number;
  sortOrder: number;
  createdAt: string;
}

export interface Application {
  id: string;
  receivedAt: string;
  status: ApplicationStatus;
  /** Optional link to `Leads.Lead_ID` when `ho_application.Lead_ID` exists (preserved on server sync). */
  leadId?: string | null;
  /** Telangana district key from geo directory (`district_key`). */
  district: string;
  /** Revenue mandal (demo / future API). */
  mandal: string;
  /** Village or habitation (demo / future API). */
  village: string;
  /** Beneficiary site — used for PO ship-to print only. */
  sitePlot?: string;
  siteLatitude?: string;
  siteLongitude?: string;
  siteState?: string;
  holdReasons: string[];
  mobile: MobilePayload;
  validations: Validations;
  ho: HoFields;
  workDoneBills: WorkDoneBill[];
  /** After ICICI/MOU and bill checks — links a master subcontractor to this beneficiary (optional work order) */
  subcontractor: SubcontractorLinkage;
  /** Assigned engineer from Engineer master (optional). */
  engineer: EngineerLinkage;
  /** Progress against global milestone templates (synced from master). */
  milestoneProgress: ApplicationMilestoneProgress[];
  /**
   * Logged actual calendar days per construction timeline phase (keys = `ConstructionTimelinePhase.id` from master).
   * Omitted keys = not yet reported from site / MIS.
   */
  constructionTimelineActualDays?: Partial<Record<string, number>>;
  /** phaseId → "YYYY-MM-DD" actual end (approval) date for each approved phase. */
  constructionTimelineActualDates?: Partial<Record<string, string>>;
  /** phaseId → "YYYY-MM-DD" actual start date saved by the subcontractor before photo submission. */
  constructionTimelineActualStartDates?: Partial<Record<string, string>>;
  /**
   * YYYY-MM-DD date set by the Project Initiation page. Acts as Day 0 for the
   * work-progress timeline — phase planned/actual dates are calculated from this.
   */
  plannedStartDate?: string | null;
  /** ID of the `project_initiation` schedule this application belongs to. */
  initiationId?: string | null;
}
